import { useRef } from 'react';
import {
    useMutation,
    useQueryClient,
    UseMutationOptions,
    UseMutationResult,
    MutateOptions,
    QueryKey,
} from 'react-query';
import {
    MutationMode,
    undoableEventEmitter,
    useDataProvider,
    DataProvider,
} from 'react-admin';
import cloneDeep from 'lodash/cloneDeep';

import {
    GetChildNodesResult,
    GetRootNodesResult,
    GetTreeResult,
    MoveAsNthSiblingOfOfParams,
    MoveAsNthSiblingOfOfResult,
    TreeDataProvider,
    TreeRecord,
} from '../types';

/**
 * Get a callback to call the dataProvider.moveAsNthChildOf() method, the result and the loading state.
 *
 * @param {string} resource
 * @param {Params} params The moveAsNthChildOf parameters { id, previousData }
 * @param {Object} options Options object to pass to the queryClient.
 * May include side effects to be executed upon success or failure, e.g. { onSuccess: () => { refresh(); } }
 * May include a mutation mode (optimistic/pessimistic/undoable), e.g. { mutationMode: 'undoable' }
 *
 * @typedef Params
 * @prop params.id The resource identifier, e.g. 123
 * @prop params.previousData The record before the update is applied
 *
 * @returns The current mutation state. Destructure as [moveAsNthChildOf, { data, error, isLoading }].
 *
 * The return value updates according to the request state:
 *
 * - initial: [moveAsNthChildOf, { isLoading: false, isIdle: true }]
 * - start:   [moveAsNthChildOf, { isLoading: true }]
 * - success: [moveAsNthChildOf, { data: [data from response], isLoading: false, isSuccess: true }]
 * - error:   [moveAsNthChildOf, { error: [error from response], isLoading: false, isError: true }]
 *
 * The moveAsNthChildOf() function must be called with a resource and a parameter object: moveAsNthChildOf(resource, { id, previousData, meta }, options)
 *
 * This hook uses react-query useMutation under the hood.
 * This means the state object contains mutate, isIdle, reset and other react-query methods.
 *
 * @see https://react-query.tanstack.com/reference/useMutation
 *
 * @example // set params when calling the moveAsNthChildOf callback
 *
 * import { useMoveAsNthSiblingOf } from '@react-admin/ra-tree';
 *
 * const MoveAsNthChildOfButton = ({ record }) => {
 *     const [moveAsNthChildOf, { isLoading, error }] = useMoveAsNthSiblingOf();
 *     const handleClick = () => {
 *         moveAsNthChildOf('nodes', { id: record.id }, record)
 *     }
 *     if (error) { return <p>ERROR</p>; }
 *     return <button disabled={isLoading} onClick={handleClick}>Delete</div>;
 * };
 *
 * @example // set params when calling the hook
 *
 * import { useMoveAsNthSiblingOf } from '@react-admin/ra-tree';
 *
 * const DeleteButton = ({ record }) => {
 *     const [moveAsNthChildOf, { isLoading, error }] = useMoveAsNthSiblingOf('nodes', { id: record.id }, record);
 *     if (error) { return <p>ERROR</p>; }
 *     return <button disabled={isLoading} onClick={() => moveAsNthChildOf()}>Delete</button>;
 * };
 *
 * @example // TypeScript
 * const [delete, { data }] = useMoveAsNthSiblingOf<Product>('products', { id, previousData: product });
 *                    \-- data is Product
 */
export const useMoveAsNthSiblingOf = <
    RecordType extends TreeRecord = TreeRecord
>(
    resource?: string,
    params: Partial<MoveAsNthSiblingOfOfParams<RecordType>> = {
        source: undefined,
        destination: undefined,
        position: undefined,
        meta: undefined,
    },
    options: UseMoveAsNthSiblingOfOptions<RecordType> = {}
): UseMoveAsNthSiblingOfResult<RecordType> => {
    const dataProvider = useDataProvider<TreeDataProvider & DataProvider>();
    const queryClient = useQueryClient();
    const { source, destination, position } = params;
    const { mutationMode = 'undoable', ...reactMutationOptions } = options;
    const mode = useRef<MutationMode>(mutationMode);
    const paramsRef =
        useRef<Partial<MoveAsNthSiblingOfOfParams<RecordType>>>(params);
    const snapshot = useRef<Snapshot>([]);

    const updateCache = ({
        resource,
        position,
        source,
        destination,
    }: {
        resource: string;
        position: number;
        source: RecordType;
        destination: RecordType;
    }) => {
        // hack: only way to tell react-query not to fetch this query for the next 5 seconds
        // because setQueryData doesn't accept a stale time option
        const now = Date.now();
        const updatedAt = mode.current === 'undoable' ? now + 5 * 1000 : now;

        const updateCollection = (old: RecordType[]): RecordType[] => {
            let newRecords = cloneDeep(old) as RecordType[];

            const oldParent = newRecords.find(record =>
                record.children.includes(source.id)
            );
            const newParent = newRecords.find(record =>
                record.children.includes(destination.id)
            );

            // if source and dest come from the same parent, and if the source was moved to a lower position,
            // the final position will be position - 1
            let finalPosition = position;
            if (
                oldParent &&
                newParent &&
                oldParent.id === newParent.id &&
                oldParent.children.findIndex(child => child === source.id) <
                    oldParent.children.findIndex(
                        child => child === destination.id
                    )
            ) {
                finalPosition = position - 1;
            }

            if (oldParent) {
                // Remove the node from its old parent
                oldParent.children = oldParent.children.filter(
                    child => child !== source.id
                );
            } else {
                // It's a root node
                newRecords = newRecords.filter(
                    record => record.id !== source.id
                );
            }

            // Add the node to its new parent at correct position
            if (newParent) {
                newParent.children = [
                    ...newParent.children.slice(0, finalPosition),
                    source.id,
                    ...newParent.children.slice(finalPosition),
                ];
            } else {
                // It's a root node
                newRecords = newRecords.filter(
                    record => record.id !== source.id
                );
                newRecords = [
                    ...newRecords.slice(0, position),
                    source,
                    ...newRecords.slice(position),
                ];
            }

            return newRecords;
        };

        queryClient.setQueriesData(
            [resource, 'getTree'],
            (res: GetTreeResult<RecordType>['data']) =>
                res && res.length > 0 ? updateCollection(res) : res,
            { updatedAt }
        );
        queryClient.setQueriesData(
            [resource, 'getRootNodes'],
            (res: GetRootNodesResult<RecordType>['data']) =>
                res && res.length > 0 ? updateCollection(res) : res,
            { updatedAt }
        );
        queryClient.setQueriesData(
            [resource, 'getChildNodes'],
            (res: GetChildNodesResult<RecordType>['data']) =>
                res && res.length > 0 ? updateCollection(res) : res,
            { updatedAt }
        );
    };

    const mutation = useMutation<
        MoveAsNthSiblingOfOfResult<RecordType>['data'],
        Error,
        Partial<UseMoveAsNthSiblingOfParams<RecordType>>
    >(
        ({
            resource: callTimeResource = resource,
            position: callTimePosition = paramsRef.current.position,
            source: callTimeSource = paramsRef.current.source,
            destination: callTimeDestination = paramsRef.current.destination,
            meta: callTimeMeta = paramsRef.current.meta,
        } = {}) =>
            dataProvider
                .moveAsNthSiblingOf<RecordType>(callTimeResource, {
                    position: callTimePosition,
                    source: callTimeSource,
                    destination: callTimeDestination,
                    meta: callTimeMeta,
                })
                .then(({ data }) => data),
        {
            ...reactMutationOptions,
            onMutate: async variables => {
                if (reactMutationOptions.onMutate) {
                    const userContext =
                        (await reactMutationOptions.onMutate(variables)) || {};
                    return {
                        snapshot: snapshot.current,
                        // @ts-ignore
                        ...userContext,
                    };
                } else {
                    // Return a context object with the snapshot value
                    return { snapshot: snapshot.current };
                }
            },
            onError: (error, variables, context: { snapshot: Snapshot }) => {
                if (
                    mode.current === 'optimistic' ||
                    mode.current === 'undoable'
                ) {
                    // If the mutation fails, use the context returned from onMutate to rollback
                    context.snapshot.forEach(([key, value]) => {
                        queryClient.setQueryData(key, value);
                    });
                }

                if (reactMutationOptions.onError) {
                    return reactMutationOptions.onError(
                        error,
                        variables,
                        context
                    );
                }
                // call-time error callback is executed by react-query
            },
            onSuccess: (data, variables = {}, context) => {
                if (mode.current === 'pessimistic') {
                    // update the getOne and getList query cache with the new result
                    const {
                        resource: callTimeResource = resource,
                        position: callTimePosition = position,
                        source: callTimeSource = source,
                        destination: callTimeDestination = destination,
                    } = variables;
                    updateCache({
                        resource: callTimeResource,
                        position: callTimePosition,
                        source: callTimeSource,
                        destination: callTimeDestination,
                    });

                    if (reactMutationOptions.onSuccess) {
                        reactMutationOptions.onSuccess(
                            data,
                            variables,
                            context
                        );
                    }
                    // call-time success callback is executed by react-query
                }
            },
            onSettled: (
                data,
                error,
                variables = {},
                context: { snapshot: Snapshot }
            ) => {
                if (
                    mode.current === 'optimistic' ||
                    mode.current === 'undoable'
                ) {
                    // Always refetch after error or success:
                    context.snapshot.forEach(([key]) => {
                        queryClient.invalidateQueries(key);
                    });
                }

                if (reactMutationOptions.onSettled) {
                    return reactMutationOptions.onSettled(
                        data,
                        error,
                        variables,
                        context
                    );
                }
            },
        }
    );

    const mutate = async (
        callTimeResource: string = resource,
        callTimeParams: Partial<MoveAsNthSiblingOfOfParams<RecordType>> = {},
        callTimeOptions: MutateOptions<
            RecordType,
            Error,
            Partial<UseMoveAsNthSiblingOfParams<RecordType>>,
            unknown
        > & { mutationMode?: MutationMode } = {}
    ) => {
        const { mutationMode, onSuccess, onSettled, onError } = callTimeOptions;

        // store the hook time params *at the moment of the call*
        // because they may change afterwards, which would break the undoable mode
        // as the previousData would be overwritten by the optimistic update
        paramsRef.current = params;

        if (mutationMode) {
            mode.current = mutationMode;
        }

        if (mode.current === 'pessimistic') {
            return mutation.mutate(
                { resource: callTimeResource, ...callTimeParams },
                { onSuccess, onSettled, onError }
            );
        }

        const {
            position: callTimePosition = position,
            source: callTimeSource = source,
            destination: callTimeDestination = destination,
        } = callTimeParams;

        // optimistic update as documented in https://react-query.tanstack.com/guides/optimistic-updates
        // except we do it in a mutate wrapper instead of the onMutate callback
        // to have access to success side effects

        const queryKeys = [
            [callTimeResource, 'getTree'],
            [callTimeResource, 'getRootNodes'],
            [callTimeResource, 'getChildNodes'],
        ];

        /**
         * Snapshot the previous values via queryClient.getQueriesData()
         *
         * The snapshotData ref will contain an array of tuples [query key, associated data]
         *
         * @example
         * [
         *   [['posts', 'getList'], { data: [{ id: 1, title: 'Hello' }], total: 1 }],
         *   [['posts', 'getMany'], [{ id: 1, title: 'Hello' }]],
         * ]
         *
         * @see https://react-query.tanstack.com/reference/QueryClient#queryclientgetqueriesdata
         */
        snapshot.current = queryKeys.reduce(
            (prev, curr) => prev.concat(queryClient.getQueriesData(curr)),
            [] as Snapshot
        );

        // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
        await Promise.all(
            snapshot.current.map(([key]) => queryClient.cancelQueries(key))
        );

        // Optimistically update to the new value
        updateCache({
            resource: callTimeResource,
            position: callTimePosition,
            source: callTimeSource,
            destination: callTimeDestination,
        });

        // run the success callbacks during the next tick
        if (onSuccess) {
            setTimeout(
                () =>
                    onSuccess(
                        undefined,
                        { resource: callTimeResource, ...callTimeParams },
                        { snapshot: snapshot.current }
                    ),
                0
            );
        }
        if (reactMutationOptions.onSuccess) {
            setTimeout(
                () =>
                    reactMutationOptions.onSuccess(
                        undefined,
                        { resource: callTimeResource, ...callTimeParams },
                        { snapshot: snapshot.current }
                    ),
                0
            );
        }

        if (mode.current === 'optimistic') {
            // call the mutate method without success side effects
            return mutation.mutate(
                { resource: callTimeResource, ...callTimeParams },
                { onSettled, onError }
            );
        } else {
            // undoable mutation: register the mutation for later
            undoableEventEmitter.once('end', ({ isUndo }) => {
                if (isUndo) {
                    // rollback
                    snapshot.current.forEach(([key, value]) => {
                        queryClient.setQueryData(key, value);
                    });
                } else {
                    // call the mutate method without success side effects
                    mutation.mutate(
                        { resource: callTimeResource, ...callTimeParams },
                        { onSettled, onError }
                    );
                }
            });
        }
    };

    return [mutate, mutation];
};

type Snapshot = [key: QueryKey, value: any][];

export interface UseMoveAsNthSiblingOfParams<
    RecordType extends TreeRecord = TreeRecord
> extends Partial<MoveAsNthSiblingOfOfParams<RecordType>> {
    resource?: string;
}

export type UseMoveAsNthSiblingOfOptions<
    RecordType extends TreeRecord = TreeRecord
> = UseMutationOptions<
    RecordType,
    Error,
    Partial<UseMoveAsNthSiblingOfParams<RecordType>>
> & { mutationMode?: MutationMode };

export type UseMoveAsNthSiblingOfResult<
    RecordType extends TreeRecord = TreeRecord
> = [
    (
        resource?: string,
        params?: Partial<MoveAsNthSiblingOfOfParams<RecordType>>,
        options?: MutateOptions<
            RecordType,
            Error,
            Partial<UseMoveAsNthSiblingOfParams<RecordType>>,
            unknown
        > & { mutationMode?: MutationMode }
    ) => Promise<void>,
    UseMutationResult<
        MoveAsNthSiblingOfOfResult<RecordType>['data'],
        Error,
        Partial<MoveAsNthSiblingOfOfParams<RecordType> & { resource?: string }>,
        unknown
    >
];
